import * as THREE from 'three';
import Product from '../Product';
import ProductClearanceLabel from './ProductClearanceLabel';
import { useNavigation } from '../../function/navigation';
import { UnitConversion } from '../../inputs/UnitConversion';

function SizeCheckUpperEnd({
  inputs,
  result,
  convRatio,
  drawingBaseDimensions,
}) {
  const { nav } = useNavigation();
  const inclination = THREE.MathUtils.degToRad(result.slope_angle_center);
  const inclination_db = inclination / 2;
  const heightDiffUE =
    Math.tan(inclination) * result.ue_angular +
    Math.tan(inclination_db) * result.ue_double_bend_value;
  const targetY =
    convRatio * (drawingBaseDimensions.h3 - heightDiffUE + result.pitch / 2);

  const reverseConfig = ['A', 'C', 'E', 'G', 'L'].includes(
    drawingBaseDimensions.configuration
  )
    ? 1
    : -1;

  const configAng = THREE.MathUtils.degToRad(
    reverseConfig === 1
      ? drawingBaseDimensions.configuration_angle
      : 360 - drawingBaseDimensions.configuration_angle
  );

  let productToShow = inputs.product_tbh[nav.focusedProduct - 1];
  if (productToShow === undefined) return;
  const rectShape = productToShow.product_shape !== 'round';
  if (!rectShape) {
    productToShow.length = productToShow.diameter;
    productToShow.width = productToShow.diameter;
  }

  const posProdz = Math.min(
    result.u1 - productToShow.length / 2,
    drawingBaseDimensions.central_diam / 2
  );

  let prodInclination;
  let prodBaseHeight;
  if (posProdz <= result.ue_angular) {
    prodInclination = -inclination;
    prodBaseHeight = posProdz * Math.tan(inclination);
  } else if (posProdz <= result.ue_angular + result.ue_double_bend_value) {
    prodInclination = -inclination_db;
    prodBaseHeight =
      result.ue_angular * Math.tan(inclination) +
      (posProdz - result.ue_angular) * Math.tan(inclination_db);
  } else {
    prodInclination = 0;
    prodBaseHeight = heightDiffUE;
  }

  const offsetDiff =
    (drawingBaseDimensions.offset_ue_outside -
      drawingBaseDimensions.offset_ue_inside) /
    2;

  const prodYpos =
    (prodBaseHeight - result.pitch / 2 + productToShow.height / 2) * convRatio;

  const posX = (drawingBaseDimensions.central_diam / 2) * convRatio;
  const textSize = 20 / drawingBaseDimensions.nbr_windings;
  const position = [
    posX * Math.cos(configAng),
    targetY,
    posX * Math.sin(configAng),
  ];

  const horClearance =
    result.ue_horizontal_clearance?.[nav.focusedProduct] || 0;

  const { displayedValue: displayedHorClearance } = UnitConversion(
    horClearance,
    inputs.unit_system,
    'mm'
  );

  return (
    <>
      <group
        rotation={[
          0,
          Math.PI / 2 - (reverseConfig * Math.PI) / 2 - configAng,
          0,
        ]}
        position={position}
        rotation-order="YXZ"
      >
        <group
          rotation={[prodInclination, 0, 0]}
          position={[
            reverseConfig *
              ((result.ue_horizontal_clearance?.[nav.focusedProduct] ?? 0) / 2 +
                offsetDiff) *
              convRatio,
            prodYpos,
            posProdz * convRatio,
          ]}
        >
          <Product
            result={result}
            convRatio={convRatio}
            product={productToShow}
            t={0}
            fixedPosition={true}
            drawingBaseDimensions={drawingBaseDimensions}
          />
        </group>
        <mesh
          rotation={[prodInclination, 0, 0]}
          position={[
            reverseConfig * (-productToShow.width / 2 + offsetDiff) * convRatio,
            prodYpos,
            posProdz * convRatio,
          ]}
        >
          <boxGeometry
            args={[
              Math.abs(horClearance) * convRatio * 1.05,
              productToShow.height * convRatio * 1.05,
              productToShow.length * 1.05 * convRatio,
            ]}
          />
          <meshBasicMaterial
            color={horClearance > 0 ? 'green' : 'red'}
            transparent={true}
            opacity={0.35}
            side={THREE.DoubleSide}
          />
        </mesh>
      </group>
      <group
        position={[
          (drawingBaseDimensions.central_diam / 2 - productToShow.width / 2) *
            convRatio *
            Math.cos(configAng) +
            posProdz *
              convRatio *
              Math.sin(Math.PI * 2 - configAng) *
              reverseConfig,
          targetY,
          (drawingBaseDimensions.central_diam / 2 - productToShow.width / 2) *
            convRatio *
            Math.sin(configAng) +
            posProdz *
              convRatio *
              Math.cos(Math.PI * 2 - configAng) *
              reverseConfig,
        ]}
      >
        <ProductClearanceLabel
          value={displayedHorClearance}
          reverseConfig={-1}
          textSize={textSize}
        />
      </group>
    </>
  );
}

export default SizeCheckUpperEnd;
